import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { ConfirmModal } from '../../../components/common/Modal';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { AlertCircle, Info, CheckCircle, AlertTriangle } from 'lucide-react';
import { initializeLegacyBridge } from '../utils/legacyBridge';
const DialogContext = createContext(undefined);
let toastIdCounter = 0;
export const DialogProvider = ({ children }) => {
    const [state, setState] = useState({
        type: null,
        open: false,
        options: {},
        resolve: null,
    });
    const [toasts, setToasts] = useState([]);
    const confirm = useCallback((options) => {
        return new Promise((resolve) => {
            setState({
                type: 'confirm',
                open: true,
                options,
                resolve,
            });
        });
    }, []);
    const info = useCallback((options) => {
        return new Promise((resolve) => {
            setState({
                type: 'info',
                open: true,
                options,
                resolve,
            });
        });
    }, []);
    const quickConfirm = useCallback((question) => {
        return confirm({
            title: question,
            confirmText: 'Yes',
            cancelText: 'Cancel',
            variant: 'default',
        });
    }, [confirm]);
    const toast = useCallback((options) => {
        const id = ++toastIdCounter;
        const duration = options.duration ?? 3000;
        setToasts((prev) => [
            ...prev,
            { id, message: options.message, variant: options.variant || 'info' },
        ]);
        // Start exit animation before removal
        setTimeout(() => {
            setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, exiting: true } : t)));
        }, duration - 300);
        // Remove after animation
        setTimeout(() => {
            setToasts((prev) => prev.filter((t) => t.id !== id));
        }, duration);
    }, []);
    // Initialize legacy bridge when provider mounts
    useEffect(() => {
        initializeLegacyBridge({ confirm, info, quickConfirm });
    }, [confirm, info, quickConfirm]);
    const handleClose = useCallback(() => {
        if (state.resolve) {
            if (state.type === 'confirm') {
                state.resolve(false);
            }
            else if (state.type === 'info') {
                state.resolve(undefined);
            }
        }
        setState({ type: null, open: false, options: {}, resolve: null });
    }, [state]);
    const handleConfirm = useCallback(() => {
        if (state.resolve) {
            if (state.type === 'confirm') {
                state.resolve(true);
            }
            else if (state.type === 'info') {
                state.resolve(undefined);
            }
        }
        setState({ type: null, open: false, options: {}, resolve: null });
    }, [state]);
    const getIcon = (variant) => {
        switch (variant) {
            case 'success':
                return _jsx(CheckCircle, { className: "h-5 w-5 text-green-600" });
            case 'warning':
                return _jsx(AlertTriangle, { className: "h-5 w-5 text-yellow-600" });
            case 'error':
                return _jsx(AlertCircle, { className: "h-5 w-5 text-red-600" });
            default:
                return _jsx(Info, { className: "h-5 w-5 text-blue-600" });
        }
    };
    return (_jsxs(DialogContext.Provider, { value: { confirm, info, quickConfirm, toast }, children: [children, _jsx("div", { className: "fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] flex flex-col gap-2 pointer-events-none", children: toasts.map((t) => (_jsxs("div", { className: `
              flex items-center gap-2 px-4 py-2 rounded-lg shadow-lg pointer-events-auto
              transition-all duration-300 ease-out
              ${t.exiting ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'}
              ${t.variant === 'success' ? 'bg-green-600 text-white' : ''}
              ${t.variant === 'error' ? 'bg-red-600 text-white' : ''}
              ${t.variant === 'warning' ? 'bg-yellow-500 text-black' : ''}
              ${t.variant === 'info' ? 'bg-blue-600 text-white' : ''}
            `, children: [t.variant === 'success' && _jsx(CheckCircle, { className: "h-4 w-4 flex-shrink-0" }), t.variant === 'error' && _jsx(AlertCircle, { className: "h-4 w-4 flex-shrink-0" }), t.variant === 'warning' && _jsx(AlertTriangle, { className: "h-4 w-4 flex-shrink-0" }), t.variant === 'info' && _jsx(Info, { className: "h-4 w-4 flex-shrink-0" }), _jsx("span", { className: "text-sm font-medium", children: t.message })] }, t.id))) }), state.type === 'confirm' && (_jsx(ConfirmModal, { open: state.open, onClose: handleClose, onConfirm: handleConfirm, title: state.options.title, description: state.options.description, confirmText: state.options.confirmText, cancelText: state.options.cancelText, confirmVariant: state.options.variant })), state.type === 'info' && (_jsx(Dialog, { open: state.open, onOpenChange: handleClose, children: _jsxs(DialogContent, { className: "max-w-md", children: [_jsxs(DialogHeader, { children: [state.options.title && (_jsxs(DialogTitle, { className: "flex items-center gap-2", children: [getIcon(state.options.variant), state.options.title] })), _jsx(DialogDescription, { className: "pt-2", children: state.options.message })] }), _jsx(DialogFooter, { children: _jsx(Button, { onClick: handleConfirm, className: "w-full", children: state.options.dismissText || 'Dismiss' }) })] }) }))] }));
};
/**
 * Hook to access dialog functions
 *
 * @example
 * const { confirm, info } = useDialog();
 *
 * // Confirmation dialog
 * const handleDelete = async () => {
 *   const confirmed = await confirm({
 *     title: 'Delete structure?',
 *     description: 'This action cannot be undone.',
 *     confirmText: 'Delete',
 *     variant: 'destructive'
 *   });
 *   if (confirmed) {
 *     // Delete logic
 *   }
 * };
 *
 * // Info dialog
 * await info({
 *   title: 'Success',
 *   message: 'Structure deleted successfully',
 *   variant: 'success'
 * });
 */
export const useDialog = () => {
    const context = useContext(DialogContext);
    if (!context) {
        throw new Error('useDialog must be used within a DialogProvider');
    }
    return context;
};
